<script setup lang="ts">
import { computed } from 'vue'
import sortBy from 'lodash/sortBy'
import reverse from 'lodash/reverse'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import {
  isDonorAddedDonation,
  sumDonationAmounts,
} from '@/helpers/taxPackDonations'
import DonationListRow from '@/components/dashboard/ui/DonationListRow.vue'
import type { DonorDonation, DonorFinancialYear } from '@/types/donorFinancialYear'

const props = defineProps<{
  financialYear: DonorFinancialYear
  manualDonations?: DonorDonation[]
}>()

const swiftaidDonations = computed(() =>
  reverse(
    sortBy(
      props.financialYear.donations.filter((d) => !isDonorAddedDonation(d)),
      'time'
    )
  )
)

const addedDonations = computed(() =>
  reverse(sortBy(props.manualDonations ?? [], 'time'))
)

const allDonations = computed(() =>
  reverse(
    sortBy([...swiftaidDonations.value, ...addedDonations.value], 'time')
  )
)

const combinedIntroKey = computed(() => {
  const count = allDonations.value.length
  if (!count) return null
  return count === 1
    ? 'Dashboard.Statements.ListIntro.CombinedOne'
    : 'Dashboard.Statements.ListIntro.CombinedMany'
})

const addedIntroKey = computed(() => {
  const count = addedDonations.value.length
  if (!count) return null
  return count === 1
    ? 'Dashboard.Statements.ListIntro.AddedOne'
    : 'Dashboard.Statements.ListIntro.AddedMany'
})

const combinedIntroCount = computed(() => allDonations.value.length)
const combinedIntroAmount = computed(() =>
  formatMoneyFromPence(sumDonationAmounts(allDonations.value))
)

const addedIntroCount = computed(() => addedDonations.value.length)
const addedIntroAmount = computed(() =>
  formatMoneyFromPence(sumDonationAmounts(addedDonations.value))
)
</script>

<template>
  <div class="sa-stmt-items">
    <p v-if="combinedIntroKey" class="sa-stmt-items-intro">
      <i18n-t :keypath="combinedIntroKey" tag="span">
        <template #count>
          <strong class="sa-stmt-items-intro__emphasis">{{ combinedIntroCount }}</strong>
        </template>
        <template #amount>
          <strong class="sa-stmt-items-intro__emphasis">{{ combinedIntroAmount }}</strong>
        </template>
      </i18n-t>
    </p>
    <p v-if="addedIntroKey" class="sa-stmt-items-intro">
      <i18n-t :keypath="addedIntroKey" tag="span">
        <template #count>
          <strong class="sa-stmt-items-intro__emphasis">{{ addedIntroCount }}</strong>
        </template>
        <template #amount>
          <strong class="sa-stmt-items-intro__emphasis">{{ addedIntroAmount }}</strong>
        </template>
      </i18n-t>
    </p>
    <p v-if="!allDonations.length" class="sa-stmt-items-empty">
      {{ $t('Dashboard.Statements.NoDonationsInYear') }}
    </p>
    <div v-else class="sa-donation-list sa-donation-list--statement-full" role="list">
      <DonationListRow
        v-for="donation in allDonations"
        :key="donation.id"
        :donation="donation"
      />
    </div>
  </div>
</template>
